import {
    Article,
    Browser,
    Calendar as CalendarIcon,
    CaretDown,
    ChartBar,
    ChartLine,
    ChartPie,
    ChatCenteredDots,
    CheckSquare,
    CircleWavyQuestion,
    Clock,
    CreditCard,
    Disc,
    EyeSlash,
    FileArrowDown,
    FileArrowUp,
    FileHtml,
    FilePdf,
    Files as FilesIcon,
    FlowArrow,
    GridFour,
    GridNine,
    Hexagon,
    Image as ImageIcon,
    LinkSimple,
    ListBullets,
    List as ListIcon,
    ListNumbers,
    PencilSimple,
    Presentation as PresentationIcon,
    Password,
    Percent,
    QrCode,
    Scan,
    Signature,
    ToggleLeft,
    CaretUpDown,
    Textbox,
} from '@phosphor-icons/react';
import { SYSTEM_TYPE_NAME_ID_MAPPINGS } from '../../constants/system';
import translations from '../../translations';
import type { PageComponent } from '../../types/page';
import type { ComponentRegistry } from '../../types/pageEditor';
import { columnTypes } from './components/gui/componentSuggest/constants';
import AIChat from './components/preview/AIChat';
import Calendar from './components/preview/Calendar';
import {
    BarChartComponent,
    DoughnutChartComponent,
    LineChartComponent,
    PieChartComponent,
    PolarChartComponent,
} from './components/preview/ChartComponent';
import Checkbox from './components/preview/Checkbox';
import CheckboxList from './components/preview/CheckboxList';
import DownloadButton from './components/preview/DownloadButton';
import FileUpload from './components/preview/FileUpload';
import Files from './components/preview/Files';
import HiddenInput from './components/preview/HiddenInput';
import Iframe from './components/preview/Iframe';
import Image from './components/preview/Image';
import Input from './components/preview/Input';
import List from './components/preview/List';
import Outcomes from './components/preview/Outcomes';
import Pdf from './components/preview/Pdf';
import Presentation from './components/preview/Presentation';
import Radio from './components/preview/Radio';
import RichText from './components/preview/RichText';
import Select from './components/preview/Select';
import Table from './components/preview/Table';
import Text from './components/preview/Text';
import Toggle from './components/preview/Toggle';
import CodeGenerator from './components/preview/codeGenerator/CodeGenerator';
import CodeScanner from './components/preview/codeScanner/CodeScanner';
import Hyperlink from './components/preview/hyperlink/Hyperlink';
import PaymentCard from './components/preview/paymentCard/PaymentCard';
import SignaturePad from './components/preview/signaturePad/SignaturePad';
import Tiles from './components/preview/tiles/Tiles';
import {
    COMPONENT_GROUPS,
    COMPONENT_TYPE,
    COMPONENT_CONFIGURATION_OPTIONS as cfgOption,
} from './constants';
import DataGrid from './components/preview/DataGrid';
import DateInput from './components/preview/DateInput';

export const componentRegistry: { [key: string]: ComponentRegistry } = {
    [COMPONENT_TYPE['INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['INPUT'],
        componentSuggestContentTypeFilter: [
            'ContentPassword',
            'ContentEncrypted',
            'ContentDateTime',
            'ContentDate',
            'ContentBoolean',
        ],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Input,
        ui: {
            icon: <PencilSimple />,
            caption: 'Input',
            shortcut: '---', // Maybe something like CTRL+SHIFT+I?
            // Maybe a description might not be needed if we have a link to the help page.
            description: 'Input component short description. Something something, text and stuff.',
            // Link to the help page for this type of component.
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Input_Input.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
        attributes: [
            { label: translations.PAGE_BUILDER_mask_attribute_label, value: 'mask' },
            {
                label: translations.PAGE_BUILDER_dateTimeFormat_attribute_label,
                value: 'dateTimeFormat',
            },
        ],
    },
    [COMPONENT_TYPE['NUMBER_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['NUMBER_INPUT'],
        componentSuggestContentTypeFilter: ['ContentNumber'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentNumber'],
        renderFn: Input,
        ui: {
            icon: <CaretUpDown />,
            caption: 'Number Input',
            shortcut: '---',
            description: 'An input for entering numbers.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
            cfgOption['STEP'],
            cfgOption['IS_SLIDER'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['TEXT_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['TEXT_INPUT'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentString'],
        renderFn: Input,
        ui: {
            icon: <Textbox />,
            caption: 'Text Input',
            shortcut: '---',
            description: 'An input for entering text.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['DATE_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['DATE_INPUT'],
        componentSuggestContentTypeFilter: ['ContentDate'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentDate'],
        renderFn: DateInput,
        ui: {
            icon: <CalendarIcon />,
            caption: 'Date Input',
            shortcut: '---',
            description: 'An input for entering a date without a time.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['DATE_TIME_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['DATE_TIME_INPUT'],
        componentSuggestContentTypeFilter: ['ContentDateTime'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentDateTime'],
        renderFn: DateInput,
        ui: {
            icon: <CalendarIcon />,
            caption: 'Date/Time Input',
            shortcut: '---',
            description: 'An input for entering a date with a time.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['MASKED_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['MASKED_INPUT'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentString'],
        renderFn: Input,
        ui: {
            icon: <EyeSlash />,
            caption: 'Masked Input',
            shortcut: '---',
            description: 'An input for masked text.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
            cfgOption['MASK'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE'], cfgOption['MASK']],
    },
    [COMPONENT_TYPE['PASSWORD_INPUT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['PASSWORD_INPUT'],
        componentSuggestContentTypeFilter: ['ContentPassword', 'ContentEncrypted'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentPassword', 'ContentEncrypted'],
        renderFn: Input,
        ui: {
            icon: <Password />,
            caption: 'Password Input',
            shortcut: '---',
            description: 'An input for entering a password or encrypted value.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['RADIO']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['RADIO'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.single,
        renderFn: Radio,
        ui: {
            icon: <ListBullets />,
            caption: 'Radio',
            shortcut: '---',
            description: 'Mutually exclusive option selection',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Radio.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
        ],
    },
    [COMPONENT_TYPE['CHECKBOX']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['CHECKBOX'],
        componentSuggestContentTypeFilter: ['ContentBoolean'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentBoolean'],
        renderFn: Checkbox,
        ui: {
            icon: <CheckSquare />,
            caption: 'Checkbox',
            shortcut: '---',
            description: 'True or false option selection',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Checkbox.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['CHECKBOXLIST']]: {
        base: {
            isMultiSelect: true,
        } as Partial<PageComponent>,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['CHECKBOXLIST'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.single,
        stateValueContentTypeFilter: ['ContentList'],
        renderFn: CheckboxList,
        ui: {
            icon: <CheckSquare />,
            caption: 'Checkbox List',
            shortcut: '---',
            description: 'Multi selectable list of options',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['MULTISELECT'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['TOGGLE']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['TOGGLE'],
        componentSuggestContentTypeFilter: ['ContentBoolean'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentBoolean'],
        renderFn: Toggle,
        ui: {
            icon: <ToggleLeft />,
            caption: 'Toggle',
            shortcut: '---',
            description: 'True or false option selection, larger than a checkbox for touch devices',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Toggle.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['SELECT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['SELECT'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.single,
        renderFn: Select,
        ui: {
            icon: <ListIcon />,
            caption: 'Combobox',
            shortcut: '---', // Maybe something like CTRL+SHIFT+S?
            description: 'Combobox component short description.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['HINT_VALUE'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['NATIVESELECT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['NATIVESELECT'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.single,
        renderFn: Select,
        ui: {
            icon: <CaretDown />,
            caption: 'Select',
            shortcut: '---',
            description: 'Select component short description.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['EDITABLE'],
            cfgOption['HINT_VALUE'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['DATA_SOURCE_FILTER'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['TEXTAREA']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['TEXTAREA'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentString'],
        renderFn: Text,
        ui: {
            icon: <Article />,
            caption: 'Textarea',
            shortcut: '---', // Maybe something like CTRL+SHIFT+A?
            description: 'Textarea component short description.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
            cfgOption['AUTOCOMPLETE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['CONTENT']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['CONTENT'],
        componentSuggestContentTypeFilter: ['ContentContent'],
        componentSuggestColumnType: columnTypes.none,
        stateValueContentTypeFilter: ['ContentContent'],
        renderFn: RichText,
        ui: {
            icon: <Article />,
            caption: 'Rich Text',
            shortcut: '---',
            description: 'Get rich text input from the user',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Richtext.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['TABLE']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['TABLE'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.multiple,
        renderFn: Table,
        ui: {
            icon: <Browser />,
            caption: 'Table',
            shortcut: '---', // Maybe something like CTRL+SHIFT+T?
            description: 'Table component short description.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['SEARCHABLE'],
            cfgOption['SEARCHABLEDISPLAYCOLUMNS'],
            cfgOption['SEARCHTERMPERSISTED'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['SORTABLE_COLUMNS'],
            cfgOption['EXPORT_CSV_BUTTON'],
            cfgOption['CSV_FILE_NAME'],
            cfgOption['COLUMN_COMPONENT_TYPE'],
            cfgOption['PAGINATION_BUTTONS'],
            cfgOption['PAGE_SIZE'],
            cfgOption['NO_RESULTS_MESSAGE'],
            cfgOption['SEARCH_TERM'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
        ],
    },
    [COMPONENT_TYPE['DATAGRID']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['DATAGRID'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.multiple,
        renderFn: DataGrid,
        ui: {
            icon: <GridNine />,
            caption: 'Data Grid',
            shortcut: '---', // Maybe something like CTRL+SHIFT+T?
            description: 'Data Grid component short description.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['SEARCHABLE'],
            cfgOption['SORTABLE_COLUMNS'],
            cfgOption['SEARCHABLEDISPLAYCOLUMNS'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['EDITABLE_COLUMNS'],
            cfgOption['EXPORT_CSV_BUTTON'],
            cfgOption['CSV_FILE_NAME'],
            cfgOption['COLUMN_COMPONENT_TYPE'],
            cfgOption['PIN_COLUMNS'],
            cfgOption['NO_RESULTS_MESSAGE'],
            cfgOption['PAGE_SIZE'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
        ],
    },
    [COMPONENT_TYPE['CALENDAR']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['CALENDAR'],
        componentSuggestContentTypeFilter: ['ContentList'],
        stateValueTypeElementFilter: SYSTEM_TYPE_NAME_ID_MAPPINGS['$Calendar Event'],
        componentSuggestColumnType: columnTypes.multiple,
        renderFn: Calendar,
        ui: {
            icon: <CalendarIcon />,
            caption: 'Calendar',
            shortcut: '---', // Maybe something like CTRL+SHIFT+T?
            description: 'A calendar component.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_FILTER'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
        ],
    },
    [COMPONENT_TYPE['FILEUPLOAD']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['FILEUPLOAD'],
        // Only a service populates a file upload
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: FileUpload,
        stateValueTypeElementFilter: SYSTEM_TYPE_NAME_ID_MAPPINGS.$File,
        ui: {
            icon: <FileArrowUp />,
            caption: 'File Upload',
            shortcut: '---',
            description: 'Allows users to upload file(s)',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_FileUpload.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['EDITABLE'],
            cfgOption['AUTO_UPLOAD'],
            cfgOption['FILE_DATA_SOURCE'],
            cfgOption['STATE_VALUE'],
            cfgOption['HINT_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['FILE_DATA_SOURCE']],
    },
    /** @deprecated */
    [COMPONENT_TYPE['FILES']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['FILES'],
        // Only a service populates a files
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Files,
        stateValueTypeElementFilter: SYSTEM_TYPE_NAME_ID_MAPPINGS.$File,
        dataPresentationPropertiesTypeElement: SYSTEM_TYPE_NAME_ID_MAPPINGS.$File,
        ui: {
            icon: <FilesIcon />,
            caption: 'Files',
            shortcut: '---',
            description: 'Display a list of files in a table with download links',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Files.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['MULTISELECT'],
            cfgOption['SEARCHABLE'],
            cfgOption['SEARCHABLEDISPLAYCOLUMNS'],
            cfgOption['FILE_DATA_SOURCE'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [cfgOption['NAME']],
    },
    [COMPONENT_TYPE['TILES']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['TILES'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Tiles,
        ui: {
            icon: <GridFour />,
            caption: 'Tiles',
            shortcut: '---', // Maybe something like CTRL+SHIFT+T?
            description: 'Display a list of items as a series of tiles',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['MULTISELECT'],
            cfgOption['SEARCHABLE'],
            cfgOption['SEARCHABLEDISPLAYCOLUMNS'],
            cfgOption['SEARCHTERMPERSISTED'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['NO_RESULTS_MESSAGE'],
            cfgOption['PAGE_SIZE'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['CHARTBAR']]: {
        base: null,
        group: COMPONENT_GROUPS['CHARTS'],
        type: COMPONENT_TYPE['CHARTBAR'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: BarChartComponent,
        ui: {
            icon: <ChartBar />,
            caption: 'Bar Chart',
            shortcut: '---',
            description: 'Display a bar chart.',
            help: null,
            canGoBack: true,
            heading: 'Configure Dataset',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['CHARTLINE']]: {
        base: null,
        group: COMPONENT_GROUPS['CHARTS'],
        type: COMPONENT_TYPE['CHARTLINE'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: LineChartComponent,
        ui: {
            icon: <ChartLine />,
            caption: 'Line Chart',
            shortcut: '---',
            description: 'Display a line chart.',
            help: null,
            canGoBack: true,
            heading: 'Configure Dataset',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['CHARTPIE']]: {
        base: null,
        group: COMPONENT_GROUPS['CHARTS'],
        type: COMPONENT_TYPE['CHARTPIE'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: PieChartComponent,
        ui: {
            icon: <ChartPie />,
            caption: 'Pie Chart',
            shortcut: '---',
            description: 'Display a pie chart.',
            help: null,
            canGoBack: true,
            heading: 'Configure Dataset',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['CHARTDOUGHNUT']]: {
        base: null,
        group: COMPONENT_GROUPS['CHARTS'],
        type: COMPONENT_TYPE['CHARTDOUGHNUT'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: DoughnutChartComponent,
        ui: {
            icon: <Disc />,
            caption: 'Doughnut Chart',
            shortcut: '---',
            description: 'Display a doughnut chart.',
            help: null,
            canGoBack: true,
            heading: 'Configure Dataset',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    [COMPONENT_TYPE['CHARTPOLAR']]: {
        base: null,
        group: COMPONENT_GROUPS['CHARTS'],
        type: COMPONENT_TYPE['CHARTPOLAR'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: PolarChartComponent,
        ui: {
            icon: <Hexagon />,
            caption: 'Polar Chart',
            shortcut: '---',
            description: 'Display a polar chart.',
            help: null,
            canGoBack: true,
            heading: 'Configure Dataset',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_PRESENTATION'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
        ],
    },
    /** @deprecated */
    [COMPONENT_TYPE['HIDDEN']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['HIDDEN'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: HiddenInput,
        ui: {
            icon: <EyeSlash />,
            caption: 'Hidden',
            shortcut: '---',
            description: 'Do not display anything.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Hidden.html',
        },
        configuration: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['PRESENTATION']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['PRESENTATION'],
        componentSuggestContentTypeFilter: ['ContentString', 'ContentContent'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Presentation,
        ui: {
            icon: <PresentationIcon />,
            caption: 'Presentation',
            shortcut: '---',
            description: 'Display rich text content.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Presentation.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['CONTENT'],
            cfgOption['ALERT_ROLE'],
        ],
        required: [cfgOption['NAME']],
    },
    [COMPONENT_TYPE['LIST']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['LIST'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.single,
        renderFn: List,
        ui: {
            icon: <ListNumbers />,
            caption: 'List',
            shortcut: '---',
            description: 'Display a list of items as a bullet or numbered list.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_List.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_FILTER'],
            cfgOption['DATA_PRESENTATION'],
            cfgOption['LIST_TYPE'],
        ],
        required: [
            cfgOption['NAME'],
            cfgOption['DATA_SOURCE_LIST'],
            cfgOption['DATA_SOURCE_LIST_VALUE'],
            cfgOption['DATA_SOURCE_CONNECTOR'],
            cfgOption['DATA_SOURCE_CONNECTOR_BINDING'],
            cfgOption['DATA_SOURCE_CONNECTOR_TYPE'],
        ],
    },
    [COMPONENT_TYPE['IFRAME']]: {
        base: {
            height: 500,
        },
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['IFRAME'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Iframe,
        ui: {
            icon: <FileHtml />,
            caption: 'Iframe',
            shortcut: '---',
            description: 'Display a web page.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Iframe.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['SOURCE'],
            cfgOption['WIDTH'],
            cfgOption['HEIGHT'],
        ],
        required: [cfgOption['NAME'], cfgOption['SOURCE']],
    },
    [COMPONENT_TYPE['OUTCOMES']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['OUTCOMES'],
        // Outcomes don't read in values
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Outcomes,
        ui: {
            icon: <FlowArrow />,
            caption: 'Outcomes',
            shortcut: '---',
            description: 'Display any associated outcome buttons.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Outcomes.html',
        },
        configuration: [cfgOption['NAME'], cfgOption['LABEL']],
        required: [cfgOption['NAME']],
    },
    /** @deprecated */
    [COMPONENT_TYPE['IMAGE']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['IMAGE'],
        // Images don't read in values
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Image,
        ui: {
            icon: <ImageIcon />,
            caption: 'Image',
            shortcut: '---',
            description: 'Display an image.',
            help: 'https://help.boomi.com/bundle/flow/page/c-flo-Pages_Components_Image.html',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['IMAGE_URI'],
            cfgOption['WIDTH'],
            cfgOption['HEIGHT'],
        ],
        required: [cfgOption['NAME'], cfgOption['IMAGE_URI']],
    },
    [COMPONENT_TYPE['PDF']]: {
        base: null,
        group: COMPONENT_GROUPS['DATA'],
        type: COMPONENT_TYPE['PDF'],
        // Pdf downloader not supported yet
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Pdf,
        ui: {
            icon: <FilePdf />,
            caption: 'PDF',
            shortcut: '---',
            description: 'Displays a link to a PDF document generated by the PDF connector.',
            help: 'https://help.boomi.com/bundle/flow/page/flo-PDF_Component.html',
        },
        configuration: [cfgOption['NAME'], cfgOption['LABEL'], cfgOption['STATE_VALUE']],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['PAYMENTCARD']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['PAYMENTCARD'],
        // Payment card not supported yet
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: PaymentCard,
        stateValueTypeElementFilter: SYSTEM_TYPE_NAME_ID_MAPPINGS['$Payment Card'],
        ui: {
            icon: <CreditCard />,
            caption: 'Payment Card',
            shortcut: '---',
            description: 'Get payment card (credit, debit, etc.) input from the user.',
            help: '<insert_docs_link_here>',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
            cfgOption['PAYMENT_CARD_CARD_NUMBER_LABEL'],
            cfgOption['PAYMENT_CARD_EXPIRY_DATE_LABEL'],
            cfgOption['PAYMENT_CARD_SECURITY_CODE_LABEL'],
            cfgOption['PAYMENT_CARD_POSTAL_CODE_LABEL'],
            cfgOption['PAYMENT_CARD_NAME_ON_CARD_LABEL'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['SIGNATUREPAD']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['SIGNATUREPAD'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: SignaturePad,
        stateValueContentTypeFilter: ['ContentString'],
        ui: {
            icon: <Signature />,
            caption: 'Signature Pad',
            shortcut: '---',
            description: 'Get handwritten input from the user.',
            help: '<insert_docs_link_here>',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['CODEGENERATOR']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['CODEGENERATOR'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: CodeGenerator,
        stateValueContentTypeFilter: ['ContentString'],
        ui: {
            icon: <QrCode />,
            caption: 'Code Generator',
            shortcut: '---',
            description: 'Generate and display QR codes',
            help: '<insert_docs_link_here>',
        },
        configuration: [cfgOption['NAME'], cfgOption['LABEL'], cfgOption['STATE_VALUE']],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['CODESCANNER']]: {
        base: null,
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['CODESCANNER'],
        componentSuggestContentTypeFilter: ['ContentString'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: CodeScanner,
        stateValueContentTypeFilter: ['ContentString'],
        ui: {
            icon: <Scan />,
            caption: 'Code Scanner',
            shortcut: '---',
            description: 'Enable the user to scan QR codes',
            help: '<insert_docs_link_here>',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['EDITABLE'],
            cfgOption['STATE_VALUE'],
        ],
        required: [cfgOption['NAME'], cfgOption['STATE_VALUE']],
    },
    [COMPONENT_TYPE['AICHAT']]: {
        base: {
            isMultiSelect: true,
            objectDataRequest: {
                typeElementBindingId: '00000000-0000-0000-0000-000000000000',
                typeElementId: SYSTEM_TYPE_NAME_ID_MAPPINGS['$Chat Message'],
                listFilter: null,
            },
        },
        group: COMPONENT_GROUPS['INPUT'],
        type: COMPONENT_TYPE['AICHAT'],
        componentSuggestContentTypeFilter: ['ContentList'],
        componentSuggestColumnType: columnTypes.none,
        renderFn: AIChat,
        stateValueContentTypeFilter: ['ContentList'],
        stateValueTypeElementFilter: SYSTEM_TYPE_NAME_ID_MAPPINGS['$Chat Message'],
        ui: {
            icon: <ChatCenteredDots />,
            caption: 'AI Chat',
            shortcut: '---',
            description: 'Converse with an AI chat model',
            help: '<insert_docs_link_here>',
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['HINT_VALUE'],
            cfgOption['STATE_VALUE'],
            cfgOption['DATA_SOURCE_2'],
            cfgOption['ACCOUNT_ID'],
            cfgOption['AGENT_ID'],
            cfgOption['SESSION_ID'],
            cfgOption['ASSISTANT_LABEL'],
            cfgOption['USER_LABEL'],
            cfgOption['VOICE_INPUT'],
        ],
        required: [cfgOption['NAME'], cfgOption['DATA_SOURCE_2']],
    },
    [COMPONENT_TYPE['UNKNOWN']]: {
        base: null,
        group: null,
        type: COMPONENT_TYPE['UNKNOWN'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Presentation,
        ui: {
            icon: <CircleWavyQuestion />,
            caption: '',
            shortcut: '---',
            description: '',
            help: '',
        },
        configuration: Object.values(cfgOption),
        required: [],
    },
    [COMPONENT_TYPE['DOWNLOADBUTTON']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['DOWNLOADBUTTON'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: DownloadButton,
        ui: {
            icon: <FileArrowDown />,
            caption: 'Download Button',
            shortcut: '---',
            description: 'Download a file.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['SOURCE'],
            cfgOption['FILENAME'],
        ],
        required: [cfgOption['NAME'], cfgOption['SOURCE']],
    },
    [COMPONENT_TYPE['HYPERLINK']]: {
        base: null,
        group: COMPONENT_GROUPS['CONTENT'],
        type: COMPONENT_TYPE['HYPERLINK'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        renderFn: Hyperlink,
        ui: {
            icon: <LinkSimple />,
            caption: 'Hyperlink',
            shortcut: '---',
            description: 'Add a hyperlink to a page.',
            help: null,
        },
        configuration: [
            cfgOption['NAME'],
            cfgOption['LABEL'],
            cfgOption['SOURCE'],
            cfgOption['TARGET'],
        ],
        required: [cfgOption['NAME'], cfgOption['SOURCE']],
    },
    [COMPONENT_TYPE['COLUMN_HYPERLINK']]: {
        base: null,
        group: COMPONENT_GROUPS['CUSTOM'],
        type: COMPONENT_TYPE['COLUMN_HYPERLINK'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        componentType: 'Column',
        contentType: ['ContentString'],
        renderFn: () => null,
        ui: {
            icon: <LinkSimple />,
            caption: 'Hyperlink Formatter',
            shortcut: '---',
            description: 'Convert text to clickable hyperlinks',
            help: null,
        },
        configuration: [],
        required: [],
    },
    [COMPONENT_TYPE['COLUMN_IMAGE']]: {
        base: null,
        group: COMPONENT_GROUPS['CUSTOM'],
        type: COMPONENT_TYPE['COLUMN_IMAGE'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        componentType: 'Column',
        contentType: ['ContentString'],
        renderFn: () => null,
        ui: {
            icon: <ImageIcon />,
            caption: 'Image Formatter',
            shortcut: '---',
            description: 'Display text as images using the text as image source',
            help: null,
        },
        configuration: [],
        required: [],
    },
    [COMPONENT_TYPE['COLUMN_RELATIVETIME']]: {
        base: null,
        group: COMPONENT_GROUPS['CUSTOM'],
        type: COMPONENT_TYPE['COLUMN_RELATIVETIME'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        componentType: 'Column',
        contentType: ['ContentDateTime', 'ContentDate'],
        renderFn: () => null,
        ui: {
            icon: <Clock />,
            caption: 'Relative Time',
            shortcut: '---',
            description: 'Display timestamps in relative time format (e.g., "2 hours ago")',
            help: null,
        },
        configuration: [],
        required: [],
    },
    [COMPONENT_TYPE['COLUMN_PROGRESSBAR']]: {
        base: null,
        group: COMPONENT_GROUPS['CUSTOM'],
        type: COMPONENT_TYPE['COLUMN_PROGRESSBAR'],
        componentSuggestContentTypeFilter: [],
        componentSuggestColumnType: columnTypes.none,
        componentType: 'Column',
        contentType: ['ContentNumber'],
        renderFn: () => null,
        ui: {
            icon: <Percent />,
            caption: 'Progress Bar',
            shortcut: '---',
            description: 'Display numbers as progress bars.',
            help: null,
        },
        configuration: [],
        required: [],
    },
};
